export type LogLevel = 'none' | 'error' | 'warn' | 'info'

/**
 * A sanitised representation of an error, safe to forward to external monitoring services.
 * Always contains only `name` and `message` — never a stack trace, raw response body,
 * or any other data that could carry PII or bearer tokens.
 */
export interface SanitisedError {
  name: string
  message: string
}

/**
 * Structured metadata accompanying a log event. Values are always strings —
 * never objects, arrays, or user identity/org data. Keys are limited to
 * structural metadata describing where and why the event occurred.
 */
export interface LogContext {
  /** The source function or module that produced the log event. */
  source?: string
  /** The active locale at the time of the event. */
  locale?: string
  /** Allow additional string-valued structural metadata. */
  [key: string]: string | undefined
}

export interface ErrorReporter {
  /**
   * Receives runtime errors. The `error` argument is always a {@link SanitisedError} —
   * never a raw `Error` object, stack trace, response body, or bearer token.
   * The `context` argument contains only structural string metadata via {@link LogContext};
   * user identity, tokens, and org data are never included.
   */
  captureError(error: SanitisedError, context?: LogContext): void
  /**
   * Receives warnings for expected degradations (e.g. falling back to cached data).
   * If omitted, warnings are routed to `captureError` with `{ level: 'warning' }` in the context.
   */
  captureWarning?(error: SanitisedError, context?: LogContext): void
  /**
   * Receives informational breadcrumb messages.
   * If omitted, info-level output is suppressed when a reporter is present.
   */
  captureMessage?(message: string, context?: LogContext): void
}

const LOG_LEVEL_SEVERITY: Record<LogLevel, number> = {
  none: 0,
  error: 1,
  warn: 2,
  info: 3,
}

let errorReporter: ErrorReporter | undefined
let logLevel: LogLevel = 'error'

export const setErrorReporter = (reporter: ErrorReporter | undefined) => {
  errorReporter = reporter
}

export const getErrorReporter = (): ErrorReporter | undefined => errorReporter

export const setLogLevel = (level: LogLevel) => {
  logLevel = level
}

export const getLogLevel = (): LogLevel => logLevel

const sanitiseError = (error: unknown): SanitisedError => {
  if (error instanceof Error) {
    return { name: error.name, message: error.message }
  }
  return { name: 'UnknownError', message: String(error) }
}

export const logError = (error: unknown, context?: LogContext) => {
  if (LOG_LEVEL_SEVERITY[logLevel] < LOG_LEVEL_SEVERITY.error) return

  const sanitised = sanitiseError(error)

  if (errorReporter) {
    try {
      errorReporter.captureError(sanitised, context)
    } catch {
      // Never let a broken reporter break the navbar
    }
  } else {
    console.error('@ecoonline/suite-ui-kit:', sanitised, context)
  }
}

export const logWarn = (error: unknown, context?: LogContext) => {
  if (LOG_LEVEL_SEVERITY[logLevel] < LOG_LEVEL_SEVERITY.warn) return

  const sanitised = sanitiseError(error)

  if (errorReporter?.captureWarning) {
    try {
      errorReporter.captureWarning(sanitised, context)
    } catch {
      // Never let a broken reporter break the navbar
    }
  } else if (errorReporter) {
    try {
      errorReporter.captureError(sanitised, { ...context, level: 'warning' })
    } catch {
      // Never let a broken reporter break the navbar
    }
  } else {
    console.warn('@ecoonline/suite-ui-kit:', sanitised, context)
  }
}

export const logInfo = (message: string, context?: LogContext) => {
  if (LOG_LEVEL_SEVERITY[logLevel] < LOG_LEVEL_SEVERITY.info) return

  if (errorReporter?.captureMessage) {
    try {
      errorReporter.captureMessage(message, context)
    } catch {
      // Never let a broken reporter break the navbar
    }
  } else {
    console.info('@ecoonline/suite-ui-kit:', message, context)
  }
}
